import React, { useState, useRef } from 'react';
import Dropdown from 'react-bootstrap/esm/Dropdown.js';
import utils from '../../common/utils.js';
import metrics from '../services/metrics.js';
import { Typeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';

const FILTER_DROPDOWN = 'dropdown';
const FILTER_TYPEAHEAD = 'typeahead';
const FILTER_TYPEAHEAD_THRESHOLD = 10;
const FILTER_TYPEAHEAD_MAX_MATCHES = 8;

export default function Form(props) {
	// widget model filter options
	const data = props.data;
	const widgetModel = data.widgetModel;
	const widgetOptions = widgetModel?.options;
	let selectedMockFilters = [];
	const filterValues = data.filterValues;
	const setFilterValues = data.setFilterValues;
	const setSearchQuery = data.setSearchQuery;
	const getPosts = data.getPosts;
	const setPage = data.setPage;

	// never show forms in static mode outside editor
	if (widgetModel.isStatic && !widgetModel.inEditor) {
		return '';
	}

	// if form is optionally hiden in embed, return now
	if (widgetModel.hideForm) {
		return '';
	}

	const [search, setSearch] = useState('');
	const [filterTypes, setFilterTypes] = useState({});

	// if in editor, filters
	if (widgetModel.isStatic){
		if (widgetModel.staticData?.filters) {
			selectedMockFilters = selectedMockFilters.concat(widgetModel.staticData?.filters);
		}
		if (selectedMockFilters.length) {
			selectedMockFilters = selectedMockFilters.filter((filter) => widgetOptions.filters.some((elem) => filter.id === elem.id) );
		}
	}

	// form blocks
	let blocks = [];
	let filters = 'static' === widgetModel.mode ? [].concat(selectedMockFilters) : [].concat(data.filters);

	const doSearch = () => {
		setSearchQuery(search);
		setPage(1);
		getPosts();
	};

	const clearSearch = () => {
		setSearchQuery('');
		setSearch('');
		setPage(1);
		getPosts();
	}

	// When query field is changed
	const onDropdownChange = (filterId, e) => {
		const value = String(e.currentTarget.getAttribute('data-value'));
		let formFilter = filters.find((filter) => filter.id === filterId);
		metrics.clickTrack(e, 'cc-filter|' + filterId, 'click');
		if (formFilter) {
			let currentFilterValues = {};
			let filterOption = formFilter.options.find((option) => {
				return String(option.value) === String(value);
			});
			currentFilterValues[filterId] = filterOption || null;
			// filterValues[filterId] = filterOption;
			setFilterValues({ ...filterValues, ...currentFilterValues });
			setPage(1);
			getPosts();
		}
	};

	// Render a dropdown filter
	const renderDropdownFilter = (filter) => {
		const filterId = filter.id;
		const filterSize = filter?.options?.length || 0;
		if (1 > filterSize) {
			widgetModel.debug && console.error('cc:Widget.Form: filter has no options: ', filter);
			return '';
		}
		return (
			<Dropdown
				onSelect={(key, event) => {
					onDropdownChange(filter.id, event);
				}}
			>
				<Dropdown.Toggle data-size={filterSize} aria-label={`Filter by ${filter.label}`}>{getDropdownFilterLabel(filter)}</Dropdown.Toggle>
				<Dropdown.Menu>
					<Dropdown.Item key="default" data-value="" className={getDropDownClassName(filter.id, null)}>
						{getDropdownFilterLabel(filter, true)}
					</Dropdown.Item>
					{filter.options &&
						filter.options.map((option, optionIndex) => {
							if (!option) return '';
							return (
								<Dropdown.Item
									data-name={option.label}
									data-value={option.value}
									key={filterId + '-' + optionIndex}
									className={getDropDownClassName(filter.id, option)}
								>
									{option.label}
								</Dropdown.Item>
							);
						})}
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	const getDropdownFilterLabel = (filter, showDefault) => {
		if (filterValues && !showDefault) {
			let activeOption = filterValues[filter.id];
			if (activeOption) {
				return activeOption.label;
			}
		}
		return 'Filter by ' + filter.label;
	};

	const renderTypeAheadFilter = (filter) => {
		let filterElId = widgetModel.id + '-' + filter.id;
		let ref = useRef();
		let populated = Boolean(filterValues[filter.id] && filterValues[filter.id].value);
		let currentOption = [];
		if (filterValues[filter.id]) {
			currentOption.push(filterValues[filter.id]);
		}

		return (
			<div className={'typeahead-filter' + (populated ? ' populated' : '')} id={filterElId}>
				<Typeahead
					maxLength="300"
					clearButton
					options={filter.options || []}
					autoComplete="off"
					selected={currentOption}
					placeholder={'Filter by ' + filter.label}
					aria-label={filter.label}
					name="query"
					id={'typeaheadfilter-' + filter.id}
					ref={ref}
					inputProps={{
						autoCorrect: 'off', // Safari-only
						spellCheck: false,
					}}
					onKeyDown={(e) => {
						if ('Enter' === e.key) {
							onTypeAheadEnter(filter, ref);
						}
					}}
					onFocus={(e) => {
						if (utils.isMobile()) {
							utils.scrollToElement('#' + filterElId);
						}
						ref.current.focus();
					}}
					onChange={(filterOptions) => onTypeAheadFilterChange(filter, filterOptions, ref)}
					filterBy={() => true}
					renderMenu={(results, menuProps, typeahead) => {
						results = sortTypeAheadResults(filter, typeahead.text);
						return results.length ? (
							<Menu
								innerRef={menuProps.innerRef}
								style={menuProps.style}
								id={'typeaheadfilter-' + filter.id + '-menu'}
							>
								{filterValues[filter.id] && filterValues[filter.id].value ? (
									<MenuItem key={0} position={0} option={''}>
										{'Filter by ' + filter.label}
									</MenuItem>
								) : (
									''
								)}
								{results.map((filterOption, index) => {
									let className = '';
									if (
										filterValues[filter.id] &&
										filterValues[filter.id].value === filterOption.value
									) {
										className = 'active';
									}
									return (
										<MenuItem
											option={filterOption}
											key={'typeaheadfilter-' + filter.id + '-' + filterOption.value}
											position={index + 1}
											className={className}
											data-alt-label={filterOption.alt}
										>
											{typeAheadMarkLabel(filter, filterOption, typeahead.text)}
										</MenuItem>
									);
								})}
							</Menu>
						) : (
							<Menu
								innerRef={menuProps.innerRef}
								style={menuProps.style}
								id={'typeaheadfilter-' + filter.id + '-menu'}
								key={'typeaheadfilter-' + filter.id + '-none'}
							>
								<MenuItem className="no-results disabled">{'No matching ' + filter.label}</MenuItem>
							</Menu>
						);
					}}
				></Typeahead>
				<div className="dropdown-controls" role="presentation">
					{filterValues[filter.id] && filterValues[filter.id].value ? (
						<button
							onClick={() => {
								onTypeAheadFilterChange(filter, null, ref)
								ref.current?.focus();
							}}
							aria-label={'Filter by ' + filter.label}
							className="dropdown-clear cdc-icon-close"
						/>
					) : (
						''
					)}
					<div className="dropdown-separator" role="presentation"></div>
					<button onClick={() => {
						ref.current?.toggleMenu();
						ref.current?.focus();
					}} className="dropdown-icon"
					aria-label={'Filter by ' + filter.label}/>
				</div>
			</div>
		);
	};

	//
	const onTypeAheadEnter = (filter, ref) => {
		let typeahead = ref?.current;
		let text = typeahead?.inputNode?.value || '';
		if (!typeahead || ! text) {
			return;
		}
		if (typeahead.isMenuShown) {
			let filterOption = typeahead.state?.activeItem;
			if (!filterOption && Array.isArray(typeahead.items) && typeahead.items.length) {
				while (!filterOption && typeahead.items.length) {
					filterOption = typeahead.items.shift();
				}
			}
			if (filterOption) {
				typeahead._handleMenuItemSelect(filterOption, {});
				onTypeAheadFilterChange(filter, [filterOption], ref);
				typeahead.hideMenu();
			}
		}
	};

	// once
	const onTypeAheadFilterChange = (filter, filterOptions, ref) => {
		let filterOption = Array.isArray(filterOptions) && filterOptions.length ? filterOptions[0] : null;
		let currentFilterValues = Object.assign({}, filterValues);
		if (filterOption) {
			currentFilterValues[filter.id] = filterOption;
		} else {
			delete currentFilterValues[filter.id];
			ref.current?.clear();
		}
		// filterValues[filterId] = filterOption;
		setFilterValues(currentFilterValues);
		setPage(1);
		getPosts();
	};

	// Filter and sort results
	const sortTypeAheadResults = (filter, text) => {
		let results = filter.options;
		let updateResults = [];
		if (!text) {
			return results;
		}
		// if current filter has a value, show all results like a dropdown
		if (filter?.id && filterValues[filter.id]) {
			return results;
		}
		let cleanedText = utils.cleanDiacritics(text);
		let regex1 = new RegExp('\\b' + utils.escapeRegExp(cleanedText), 'i');
		let regex2 = new RegExp(utils.escapeRegExp(cleanedText), 'i');
		results.forEach((filterOption) => {
			let label = utils.cleanDiacritics(filterOption.label || '');
			// If We have any Alt Labels, store them in Array
			if ( filterOption.alt ) {
				if ( Array.isArray( filterOption.alt ) ){
					label += ' (' + filterOption.alt.map( ( option ) => utils.cleanDiacritics( option ) ).join(', ') + ')';
				}
			}
			let match1 = label.match(regex1);
			let match2 = label.match(regex2);
			let weight = -1;
			let index = 0;
			if (match1) {
				index = match1.index;
				weight = index;
			} else if (match2) {
				index = match2.index;
				weight = index + 100;
			}
			if (0 > weight) {
				return;
			}
			filterOption.weight = weight;
			filterOption.index = index;
			updateResults.push(filterOption);
		});
		updateResults.sort((a, b) => {
			if (a.weight === b.weight) {
				return 0;
			}
			return a.weight < b.weight ? -1 : 1;
		});
		updateResults = updateResults.slice(0, FILTER_TYPEAHEAD_MAX_MATCHES);
		return updateResults;
	};

	const typeAheadMarkLabel = (filter, filterOption, text) => {
		let label = utils.cleanString(filterOption?.label);
		let index = filterOption?.index;
		text = String(text).trim();
		let textSize = text.length;
		if (!text || 'number' !== typeof index || filterValues[filter.id]) {
			return label;
		}
		// if match is on an alt, add alts to label
		if (index > label.length && Array.isArray(filterOption.alt)) {
			label += ' (' + filterOption.alt.join(', ') + ')';
		}
		return (
			<>
				{index ? label.substring(0, index) : ''}
				<b>{label.substring(index, index + textSize)}</b>
				{label.substring(index + textSize)}
			</>
		);
	};

	// Render the search field with optional filter
	const renderSearch = () => {

		return (
			<div className="input-group search">
				<input
					className="form-control search-box"
					aria-label="Search"
					value={search}
					onChange={(e) => {
						setSearch(e.currentTarget.value);
					}}
					onKeyUp={(e) => {
						if ('enter' === String(e.key).toLowerCase()) {
							metrics.clickTrack(e, 'cc-search', 'key');
							doSearch();
						}
					}}
				/>
				<button onClick={clearSearch} className={`${search.length > 0 ? 'show' : 'hide'} form-control-clear`}>&times;</button>
				<div className="input-group-append">
					<button
						className="btn btn-search search-submit search-button"
						type="button"
						onClick={(e) => {
							metrics.clickTrack(e, 'cc-search', 'click');
							doSearch();
						}}
						onKeyUp={(e) => {
							if ('enter' === String(e.key).toLowerCase()) {
								metrics.clickTrack(e, 'cc-search', 'key');
								doSearch();
							}
						}}
					>
						<span className="sr-only">Search</span>
						<i className="fi cdc-icon-magnify x24" aria-hidden="true"></i>
					</button>
				</div>
			</div>
		);
	};

	const getDropDownClassName = (filterId, option) => {
		let className = '';
		if (!option) {
			if (!filterValues[filterId]) {
				className += 'active';
			}
		} else if (filterValues[filterId] && filterValues[filterId].value === option.value) {
			className += 'active';
		}
		if (option) {
			if (option.level) {
				className += ' level-' + option.level;
			}
		}
		return className;
	};

	const renderCounts = () => {
		let message = 'Found ' + data.allTotal + ' item' + utils.pluralize(data.allTotal, 's') + '.';
		if (data.total !== data.allTotal) {
			message =
				'Found ' +
				data.total +
				' item' +
				utils.pluralize(data.total, 's') +
				' out of ' +
				data.allTotal +
				' item' +
				utils.pluralize(data.allTotal, 's') +
				'.';
		}
		// When blank, the 0 results is redundant, hide counts
		if (!data.total) {
			return '';
		}
		return <div className="counts">{message}</div>;
	};

	// build out form blocks
	if (widgetOptions.form.search) {
		blocks.push(renderSearch());
	}

	if (filters.length && !widgetModel.hideFilters) {
		let currentFilterTypes = filterTypes;
		if (!Object.keys(currentFilterTypes).length) {
			filters.forEach((filter) => {
				let filterId = filter.id;
				let optionsLength = filter.options?.length || 0;
				currentFilterTypes[filterId] =
					FILTER_TYPEAHEAD_THRESHOLD < optionsLength ? FILTER_TYPEAHEAD : FILTER_DROPDOWN;
			});
			setFilterTypes(currentFilterTypes);
		}
		filters.forEach((filter) => {
			let filterType = currentFilterTypes[filter.id] || FILTER_DROPDOWN;
			let block;
			if (FILTER_TYPEAHEAD === filterType) {
				block = renderTypeAheadFilter(filter);
			} else {
				block = renderDropdownFilter(filter);
			}
			if (block) {
				blocks.push(block);
			}
		});
	}

	if (!blocks.length) {
		return '';
	}

	return (
		<>
			<div className="widget-form">
				<div className="row">
					{blocks.map((block, blockId) => {
						return (
							<div key={'block-' + blockId} className="col-md-6">
								{block}
							</div>
						);
					})}
				</div>
			</div>
			{widgetOptions.form.counts ? renderCounts() : ''}
		</>
	);
}
